import { useEffect, useId, useState, type JSX } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $wrapNodeInElement, mergeRegister } from "@lexical/utils"
import { Input, Label, Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/shad"
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  getDOMSelectionFromTarget,
  isHTMLElement,
  type LexicalCommand,
  type LexicalEditor,
} from "lexical"

import { SimpleFormDialog } from "@/components/dialogs"
import {
  $createInlineImageNode,
  $isInlineImageNode,
  InlineImageNode,
  InlineImagePayload,
  Position,
} from "@/components/lexical/nodes/inline-image/node"

export type InsertInlineImagePayload = Readonly<InlineImagePayload>

export const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
])
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024

export const INSERT_INLINE_IMAGE_COMMAND: LexicalCommand<InlineImagePayload> =
  createCommand("INSERT_INLINE_IMAGE_COMMAND")

export const OPEN_IMAGE_DIALOG_COMMAND: LexicalCommand<void> = createCommand("OPEN_IMAGE_DIALOG_COMMAND")

export function InsertInlineImageDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor
  onClose: () => void
}): JSX.Element {
  const dialogFormId = useId()

  const [src, setSrc] = useState("")
  const [altText, setAltText] = useState("")
  const [inputMethod, setInputMethod] = useState<"upload" | "url">("upload")
  const [uploadError, setUploadError] = useState("")

  const isDisabled = src === ""

  const loadImage = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return

    if (file.type && !ALLOWED_IMAGE_TYPES.has(file.type)) {
      setUploadError("Unsupported image type. Use JPEG, PNG, GIF, WebP, or SVG.")
      return
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setUploadError("Image is too large. Maximum size is 10 MB.")
      return
    }

    setUploadError("")
    const reader = new FileReader()
    reader.onload = function () {
      if (typeof reader.result === "string") {
        setSrc(reader.result)
      }
      return ""
    }
    reader.onerror = () => setUploadError("Could not read the selected file. Please try again.")
    reader.readAsDataURL(file)
  }

  const handleOnClick = () => {
    const payload = { altText, position: "full" as Position, showCaption: false, src }
    activeEditor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, payload)
    onClose()
  }

  return (
    <SimpleFormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Insert image"
      action={{ label: "Insert", form: dialogFormId, disabled: isDisabled }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (isDisabled) return
          void handleOnClick()
        }}
      >
        <div className="space-y-2">
          <Tabs value={inputMethod} onValueChange={(v) => setInputMethod(v as "upload" | "url")} className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-2">
              <TabsTrigger value="upload">Upload</TabsTrigger>
              <TabsTrigger value="url">URL</TabsTrigger>
            </TabsList>

            <TabsContent value="upload">
              <div>
                <Label htmlFor="image-upload" className="cursor-pointer">
                  Choose Image
                </Label>
                <Input
                  id="image-upload"
                  type="file"
                  accept={[...ALLOWED_IMAGE_TYPES].join(",")}
                  onChange={(e) => loadImage(e.target.files)}
                  className="mt-2 cursor-pointer"
                />
                {uploadError && <p className="text-destructive mt-2 text-sm">{uploadError}</p>}
              </div>
            </TabsContent>

            <TabsContent value="url">
              <div>
                <Label htmlFor="image-url">Image URL</Label>
                <Input
                  id="image-url"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={src}
                  onChange={(e) => setSrc(e.target.value)}
                  className="mt-2"
                  autoFocus
                />
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <Label htmlFor="alt-text">Alt Text</Label>
            <Input
              id="alt-text"
              placeholder="Describe the image for accessibility"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
      </form>
    </SimpleFormDialog>
  )
}

export default function InlineImagePlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!editor.hasNodes([InlineImageNode])) {
      throw new Error("InlineImagePlugin: InlineImageNode not registered on editor")
    }

    return mergeRegister(
      editor.registerCommand<void>(
        OPEN_IMAGE_DIALOG_COMMAND,
        () => {
          editor.blur()
          setIsOpen(true)
          return true
        },
        COMMAND_PRIORITY_NORMAL
      ),
      editor.registerCommand<InsertInlineImagePayload>(
        INSERT_INLINE_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createInlineImageNode(payload)
          $insertNodes([imageNode])
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd()
          }

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event)
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event)
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor)
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [editor])

  return <>{isOpen && <InsertInlineImageDialog activeEditor={editor} onClose={() => setIsOpen(false)} />}</>
}

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection()
  if (!node) {
    return false
  }
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) {
    return false
  }
  dataTransfer.setData("text/plain", "_")
  dataTransfer.setData(
    "application/x-lexical-drag",
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: "image",
    })
  )

  return true
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection()
  if (!node) {
    return false
  }
  if (!canDropImage(event)) {
    event.preventDefault()
  }
  return true
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection()
  if (!node) {
    return false
  }
  const data = getDragImageData(event)
  if (!data) {
    return false
  }
  event.preventDefault()
  if (canDropImage(event)) {
    const range = getDragSelection(event)
    node.remove()
    const rangeSelection = $createRangeSelection()
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range)
    }
    $setSelection(rangeSelection)
    editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, data)
  }
  return true
}

function $getImageNodeInSelection(): InlineImageNode | null {
  const selection = $getSelection()
  if (!$isNodeSelection(selection)) {
    return null
  }
  const nodes = selection.getNodes()
  const node = nodes[0]
  return $isInlineImageNode(node) ? node : null
}

function getDragImageData(event: DragEvent): null | InsertInlineImagePayload {
  const dragData = event.dataTransfer?.getData("application/x-lexical-drag")
  if (!dragData) {
    return null
  }
  const { type, data } = JSON.parse(dragData)
  if (type !== "image") {
    return null
  }

  return data
}

declare global {
  interface DragEvent {
    rangeOffset?: number
    rangeParent?: Node
  }
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target
  return !!(
    isHTMLElement(target) &&
    !target.closest("code, span.editor-image") &&
    isHTMLElement(target.parentElement) &&
    target.parentElement.closest("div.ContentEditable__root")
  )
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range
  const domSelection = getDOMSelectionFromTarget(event.target)
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY)
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0)
    range = domSelection.getRangeAt(0)
  } else {
    throw Error("Cannot get the selection when dragging")
  }

  return range
}
